import './ModalTwoStepAuthenticationConfig';
import { alpha, Switch, useTheme } from '@mui/material';
import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FooterModalTwoStepAuthentication from './components/FooterModalTwoStepAuthentication/FooterModalTwoStepAuthentication';
import { useVerificationCodeCache } from '../../../cache/learner/verificationCodeReactiveVar';
import { CollectionActionEnum } from '../../../constants/enums/CollectionActionEnum';
import { getTestAttrs, TestIdContext } from '../../../constants/testingAttributes';
import { ModalContext } from '../../../contexts/ModalContext';
import useNotificationsHook from '../../../hooks/notificationsHook/useNotificationsHook';
import useMFAAuthenticationHook from '../../../hooks/useMFAAuthenticationHook/useMFAAuthenticationHook';
import { useQueryIsMFAEnabledByEmail } from '../../../operations/query/mfa/useIsMFAEnabledByEmail/useIsMFAEnabledByEmail';
import { COLORS } from '../../../theme/colors';
import AtomFlex from '../../atoms/AtomFlex';
import AtomGrid from '../../atoms/AtomGrid';
import AtomProgress from '../../atoms/AtomProgress';
import AtomTypography from '../../atoms/AtomTypography/AtomTypography';
import AuthenticatorBoldIcon from '../../icons/svg/AuthenticatorBoldIcon';
import InfoIcon from '../../icons/svg/InfoIcon';
import { styleConfigs } from '../../resources/styleConfigs';
import BoxWrapper from '../../wrappers/BoxWrapper';
import NewModalLayoutComponent from '../ModalComponent/NewModalLayoutComponent';
import ModalVerificationCode, { IModalVerificationCodeProps } from '../ModalVerificationCode/ModalVerificationCode';

const ModalTwoStepAuthentication: FC = () => {
  const { t } = useTranslation('ModalTwoStepAuthentication');
  const theme = useTheme();
  const { operations } = useMFAAuthenticationHook();
  const { isOpen, modalClose } = useContext(ModalContext);
  const {
    data: isMFAEnabledData,
    refetch: refetchIsMFAEnabled,
    loading: loadingIsMFAEnabled,
  } = useQueryIsMFAEnabledByEmail(undefined, { skip: !isOpen });
  const [activeSwitch, setActiveSwitch] = useState<boolean>();
  const [openModalVerificationCode, setOpenModalVerificationCode] = useState<boolean>(false);
  const [modalVerificationCodeProps, setModalVerificationCodeProps] = useState<IModalVerificationCodeProps>({});
  const { setCreateVerificationCodeId } = useVerificationCodeCache();
  const { operations: notificationOps } = useNotificationsHook();
  const [validationSuccess, setValidationSuccess] = useState<boolean>();
  const [doneButtonActive, setDoneButtonActive] = useState<boolean>();
  const lastMfaEnabledRef = useRef<boolean | null>(null);
  const prevIsOpenRef = useRef<boolean>(isOpen);

  useEffect(() => {
    if (isMFAEnabledData?.isMFAEnabledByEmail?.enabled !== undefined) {
      lastMfaEnabledRef.current = isMFAEnabledData.isMFAEnabledByEmail.enabled;
    }
  }, [isMFAEnabledData?.isMFAEnabledByEmail?.enabled]);

  const onClickOpenModalVerificationCode = async (): Promise<void> => {
    const setMFAAuthenticationResult = await operations.handleSetMFAAuthentication({ mfaAuthentication: true });

    setModalVerificationCodeProps({
      directProps: {
        verificationCodeData: {
          data: setMFAAuthenticationResult,
          setValidationSuccess,
        },
      },
    });
    setOpenModalVerificationCode(true);
    setCreateVerificationCodeId(CollectionActionEnum.VERIFICATION_CODE);
  };

  const handleDeactivateSwitch = useCallback(async (): Promise<void> => {
    await operations.handleSetMFAAuthentication({ mfaAuthentication: false });
  }, []);

  const handleSwitchChange = useCallback((): void => {
    if (isMFAEnabledData?.isMFAEnabledByEmail.enabled) {
      handleDeactivateSwitch();
    }
    setActiveSwitch(!activeSwitch);
    setDoneButtonActive(!activeSwitch);
  }, [activeSwitch, isMFAEnabledData?.isMFAEnabledByEmail.enabled]);

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;

    prevIsOpenRef.current = isOpen;

    if (wasOpen && !isOpen) {
      if (lastMfaEnabledRef.current === false && validationSuccess !== true) {
        notificationOps.addNotification('info', t('SET_OFF_DESCRIPTION_TEXT'), t('SET_OFF_TITLE_TEXT'));
      }
      setValidationSuccess(undefined);
    }
  }, [isOpen, validationSuccess, t, notificationOps]);

  useEffect(() => {
    if (!isOpen) {
      setOpenModalVerificationCode(false);
      setModalVerificationCodeProps({});
      setCreateVerificationCodeId(undefined);
      setActiveSwitch(false);
      setDoneButtonActive(false);
    }
    if (isOpen) {
      refetchIsMFAEnabled();
    }
    if (isMFAEnabledData?.isMFAEnabledByEmail.enabled) {
      setActiveSwitch(true);
    }
  }, [isOpen, isMFAEnabledData?.isMFAEnabledByEmail.enabled, refetchIsMFAEnabled]);

  const renderModalTwoStepAuthentication = (): JSX.Element => (
    <NewModalLayoutComponent
      titleHeader={t('MODAL_AUTHENTICATION_PRINCIPAL_TITLE')}
      paddingInline={styleConfigs.marginPadding.custom25remNumber}
      customFooter={
        <FooterModalTwoStepAuthentication
          disabledDoneButton={!doneButtonActive}
          handleCancelFooterModal={modalClose}
          handleDoneFooterModal={onClickOpenModalVerificationCode}
        />
      }
      hasDivider
    >
      {loadingIsMFAEnabled ? (
        <AtomGrid height={styleConfigs.dimensions.height260}>
          <AtomProgress
            visible={loadingIsMFAEnabled}
            layout="inline"
            background="transparent"
            loadingText={t('LOADING_TEXT')}
            textColor="prussianBlue"
            data-testid="circular_loader"
          />
        </AtomGrid>
      ) : (
        <AtomFlex
          py={styleConfigs.marginPadding.custom2}
          px={styleConfigs.marginPadding.custom3}
          gap={styleConfigs.marginPadding.custom4}
        >
          <AtomFlex gap={styleConfigs.marginPadding.custom2}>
            <AtomGrid display="flex" gap={styleConfigs.marginPadding.custom1}>
              <InfoIcon width={styleConfigs.dimensions.width30} height={styleConfigs.dimensions.height30} />
              <AtomTypography variant="h5">{t('MODAL_AUTHENTICATION_SUBTITLE')}</AtomTypography>
            </AtomGrid>
            <AtomTypography component="p" variant="subtitle2" fontWeight={400} color={COLORS.mistBlue}>
              {t('MODAL_AUTHENTICATION_SUBTITLE_DESCRIPTION')}
            </AtomTypography>
          </AtomFlex>
          <AtomFlex gap={styleConfigs.marginPadding.custom1}>
            <AtomGrid
              display="flex"
              justifyContent="space-between"
              bgcolor={
                activeSwitch ? alpha(COLORS.twoAuthentication, 0.4) : alpha(theme.palette.background.redViolet, 0.05)
              }
              py={styleConfigs.marginPadding.custom3}
              px={styleConfigs.marginPadding.custom2}
              gap={styleConfigs.marginPadding.custom2}
              borderRadius={styleConfigs.borders.radius1}
            >
              <AtomGrid display="flex" alignItems="center" gap={styleConfigs.marginPadding.custom1}>
                <BoxWrapper>
                  <AuthenticatorBoldIcon
                    width={styleConfigs.dimensions.width32}
                    height={styleConfigs.dimensions.height32}
                  />
                </BoxWrapper>
                <AtomTypography variant="subtitle1">
                  {activeSwitch
                    ? t('MODAL_AUTHENTICATION_ON_SWITCH_TITLE')
                    : t('MODAL_AUTHENTICATION_OFF_SWITCH_TITLE')}
                </AtomTypography>
              </AtomGrid>
              <AtomGrid>
                <Switch
                  checked={activeSwitch ?? false}
                  onChange={handleSwitchChange}
                  {...getTestAttrs(TestIdContext.MODAL, 'two_step_auth_switch', {
                    active: String(activeSwitch ?? false),
                  })}
                  sx={{
                    '.MuiSwitch-thumb': {
                      color: activeSwitch ? COLORS.greenPathCourses : undefined,
                    },
                    '.MuiSwitch-track': {
                      background: activeSwitch ? `${COLORS.greenPathCourses} !important` : undefined,
                    },
                  }}
                />
              </AtomGrid>
            </AtomGrid>
            {activeSwitch && (
              <AtomTypography variant="subtitle2" fontWeight={400} color={COLORS.mistBlue}>
                {t('MODAL_AUTHENTICATION_ON_SWITCH_DESCRIPTION')}
              </AtomTypography>
            )}
          </AtomFlex>
        </AtomFlex>
      )}
    </NewModalLayoutComponent>
  );

  const renderModalVerificationCode = (): JSX.Element => <ModalVerificationCode {...modalVerificationCodeProps} />;

  return <>{openModalVerificationCode ? renderModalVerificationCode() : renderModalTwoStepAuthentication()}</>;
};

export default ModalTwoStepAuthentication;
